import { buildGtmUrl } from './gtm-url.js';
import { hasGlobalPrivacyControlSignal } from './global-privacy-control.js';

const OPT_OUT_REJECT_MODE = 'rdp';
const signal = granted => granted ? 'granted' : 'denied';
function policyDefaultConsents(model) {
  const granted = model === 'opt-out' && !hasGlobalPrivacyControlSignal();
  return {
    measurement: granted,
    marketing: granted
  };
}
function restrictsDataProcessing(consents, model, rejectMode = OPT_OUT_REJECT_MODE) {
  return model === 'opt-out' && rejectMode === 'rdp' && !consents.marketing;
}
function mapConsentModeSignals(consents, model, rejectMode = OPT_OUT_REJECT_MODE) {
  const adStorage = consents.marketing || restrictsDataProcessing(consents, model, rejectMode);
  return {
    security_storage: 'granted',
    functionality_storage: 'denied',
    personalization_storage: 'denied',
    analytics_storage: signal(consents.measurement),
    ad_storage: signal(adStorage),
    ad_user_data: signal(adStorage),
    ad_personalization: signal(consents.marketing)
  };
}
function googleTag() {
  const target = window;
  const dataLayer = target.dataLayer = target.dataLayer || [];
  if (typeof target.gtag !== 'function') {
    target.gtag = function gtagQueueStub() {
      dataLayer.push(arguments);
    };
  }
  return {
    gtag: target.gtag,
    dataLayer
  };
}
function emitConsentModeDefault(consents, model) {
  const {
    gtag,
    dataLayer
  } = googleTag();
  const defaults = mapConsentModeSignals(policyDefaultConsents(model), model);
  const current = mapConsentModeSignals(consents, model);
  const rdp = restrictsDataProcessing(consents, model);
  gtag('consent', 'default', defaults);
  if (JSON.stringify(current) !== JSON.stringify(defaults)) {
    gtag('consent', 'update', current);
  }
  gtag('set', 'restricted_data_processing', rdp);
  dataLayer.push({
    rdp
  });
}
function emitConsentModeUpdate(consents, model) {
  const {
    gtag,
    dataLayer
  } = googleTag();
  const rdp = restrictsDataProcessing(consents, model);
  gtag('consent', 'update', mapConsentModeSignals(consents, model));
  gtag('set', 'restricted_data_processing', rdp);
  dataLayer.push({
    event: 'consent-update',
    rdp
  });
}
function buildGoogleConsentScripts({
  gtmId,
  gtmView,
  ga4Id,
  getModel,
  onError
}) {
  const safely = (mode, emit) => {
    try {
      emit();
    } catch (error) {
      onError(mode, error);
    }
  };
  const scripts = [{
    id: 'google-consent-mode',
    category: 'necessary',
    alwaysLoad: true,
    callbackOnly: true,
    onBeforeLoad: ({
      consents
    }) => safely('default', () => emitConsentModeDefault(consents, getModel())),
    onConsentChange: ({
      consents
    }) => safely('update', () => emitConsentModeUpdate(consents, getModel()))
  }];
  if (gtmId) {
    scripts.push({
      id: 'google-tag-manager',
      category: 'necessary',
      alwaysLoad: true,
      src: buildGtmUrl('gtm.js', gtmId, gtmView),
      async: true,
      onBeforeLoad: () => {
        googleTag().dataLayer.push({
          'gtm.start': new Date().getTime(),
          event: 'gtm.js'
        });
      }
    });
  }
  if (ga4Id) {
    scripts.push({
      id: 'gtag',
      category: 'measurement',
      alwaysLoad: true,
      persistAfterConsentRevoked: true,
      src: `https://www.googletagmanager.com/gtag/js?id=${ga4Id}`,
      async: true,
      onBeforeLoad: () => {
        const {
          gtag
        } = googleTag();
        gtag('js', new Date());
        gtag('config', ga4Id);
      }
    });
  }
  return scripts;
}

export { buildGoogleConsentScripts, emitConsentModeUpdate, mapConsentModeSignals, restrictsDataProcessing };
